import { useState } from "react";
import { Users, Building2, Landmark, Wallet, FolderKanban, CheckCircle2, ArrowUpRight, ArrowDownRight, Minus, CalendarDays, ArrowDownWideNarrow, RefreshCw, Search, X } from "lucide-react";
import { fmtTL, fmtNum } from "../../lib/format";
import { foldIncludes } from "../../lib/trSearch";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const AY_KISA = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const monthLabel = (ym, short) => (ym ? `${(short ? AY_KISA : AYLAR)[Number(ym.slice(5, 7)) - 1]}${short ? "" : ` ${ym.slice(0, 4)}`}` : "");

const byName = (a, b) => a.project_name.localeCompare(b.project_name, "tr");
const SORTS = {
  turnover: ["Ciroya göre", (a, b) => b.turnover - a.turnover || byName(a, b)],
  personnel: ["Çalışana göre", (a, b) => b.personnel - a.personnel || b.turnover - a.turnover || byName(a, b)],
  branches: ["Şube sayısına göre", (a, b) => b.branches - a.branches || b.turnover - a.turnover || byName(a, b)],
  atms: ["ATM sayısına göre", (a, b) => b.atms - a.atms || b.turnover - a.turnover || byName(a, b)],
};
const SORT_KEY = "dash.sort";

const staffSub = (k) => `Asıl ${k.asil} · Geçici ${k.gecici}${k.yonetim ? ` · Yönetim ${k.yonetim}` : ""}`;

const Stat = ({ icon: Icon, label, value, sub, testid, children }) => (
  <div className="flex items-start gap-2 min-w-0" data-testid={testid}>
    <Icon className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-bold font-mono text-slate-900 break-words">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 font-mono whitespace-nowrap">{sub}</div>}
      {children}
    </div>
  </div>
);

// Geçen aya göre ciro trendi: ok + yüzde; title'da iki ayın tutarı
function Trend({ p, refMonth, prevMonth }) {
  const none = !p.m_cur && !p.m_prev;
  const Icon = none || p.trend === "flat" ? Minus : p.trend === "up" ? ArrowUpRight : ArrowDownRight;
  const tone = none || p.trend === "flat" ? "text-slate-500 bg-slate-100" : p.trend === "up" ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-50";
  const pct = none ? "hareket yok" : p.trend_pct == null ? "yeni" : `${p.trend_pct > 0 ? "+" : ""}%${fmtNum(p.trend_pct, 1)}`;
  return (
    <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${tone}`} data-testid="project-card-trend" data-trend={none ? "none" : p.trend}
      title={`${monthLabel(refMonth)}: ${fmtTL(p.m_cur)} · ${monthLabel(prevMonth)}: ${fmtTL(p.m_prev)}`}>
      <Icon className="w-3 h-3" />
      <span className="font-mono">{pct}</span>
      <span className="font-normal text-slate-500">geçen aya göre</span>
    </div>
  );
}

// Son 6 ayın onaylı cirosu: saf CSS çubuk grafik (karta göre ölçekli), son ay vurgulu
function MiniBars({ months, values }) {
  const max = Math.max(...values, 0);
  return (
    <div className="mt-2.5" data-testid="project-card-chart">
      <div className="flex items-end gap-1 h-10">
        {values.map((v, i) => (
          <div key={months[i]} className="flex-1 flex flex-col justify-end h-full" title={`${monthLabel(months[i])}: ${fmtTL(v)}`} data-testid={`project-card-bar-${months[i]}`}>
            <div className={`w-full rounded-sm transition-[height] ${i === values.length - 1 ? "bg-amber-500" : "bg-amber-200"} ${!v ? "opacity-40" : ""}`} style={{ height: max ? `${Math.max((v / max) * 100, v ? 6 : 3)}%` : "3%" }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1">
        {months.map((m) => <div key={m} className="flex-1 text-center text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{monthLabel(m, true)}</div>)}
      </div>
    </div>
  );
}

function ProjectCard({ p, cards, finished }) {
  return (
    <div className={`kpi ${finished ? "opacity-80" : ""}`} data-testid={`project-card-${p.project_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-bold text-slate-900 leading-snug break-words" data-testid="project-card-name">{p.project_name}</div>
        {finished && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Bitti</span>}
      </div>
      <div className="mt-3 space-y-2.5">
        <Stat icon={Users} label="Çalışan" value={p.personnel} sub={staffSub(p)} testid="project-card-personnel" />
        <div className="grid grid-cols-2 gap-x-3">
          <Stat icon={Building2} label="Şube" value={p.branches} testid="project-card-branches" />
          <Stat icon={Landmark} label="ATM" value={p.atms} testid="project-card-atms" />
        </div>
        <div className="pt-2 border-t border-slate-100">
          <Stat icon={Wallet} label={`Onaylı Ciro (${cards.year})`} value={fmtTL(p.turnover)} testid="project-card-turnover">
            <Trend p={p} refMonth={cards.ref_month} prevMonth={cards.prev_month} />
          </Stat>
          <MiniBars months={cards.months} values={p.months} />
        </div>
      </div>
    </div>
  );
}

const Sel = ({ icon: Icon, value, onChange, children, testid, title }) => (
  <label className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700" title={title}>
    <Icon className="w-3.5 h-3.5 text-amber-600" />
    <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent outline-none font-semibold" data-testid={testid}>{children}</select>
  </label>
);

// Yönetici Genel Bakış: aktif projeler kutu kutu (yıl + sıralama seçimi, tarayıcıda hatırlanır) + seçilen yılda biten projeler ayrı bölümde; veriler odak/30 sn'de tazelenir
export default function ProjectCards({ cards, year, onYear, updatedAt, onRefresh }) {
  const [sort, setSortState] = useState(() => (SORTS[localStorage.getItem(SORT_KEY)] ? localStorage.getItem(SORT_KEY) : "turnover"));
  const setSort = (v) => { setSortState(v); localStorage.setItem(SORT_KEY, v); };
  const [q, setQ] = useState("");
  if (!cards) return null;
  const match = (list) => (q.trim() ? list.filter((p) => foldIncludes(p.project_name, q.trim())) : list);
  const active = match(cards.active), finished = match(cards.finished);
  const sorted = (list) => [...list].sort(SORTS[sort][1]);
  const Grid = ({ list, finished }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid={finished ? "project-cards-finished-grid" : "project-cards-active"}>
      {sorted(list).map((p) => <ProjectCard key={p.project_id} p={p} cards={cards} finished={finished} />)}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="project-cards">
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <FolderKanban className="w-4 h-4 text-amber-600" />
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Projeler</h2>
          <span className="text-xs text-slate-500">· {cards.active.length} aktif proje · ciro yalnız onaylı hizmetler</span>
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <label className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus-within:border-amber-500" title="Proje adıyla ara (Türkçe karakter duyarsız)">
              <Search className="w-3.5 h-3.5 text-amber-600" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje ara…" className="bg-transparent outline-none w-28 sm:w-36 placeholder:text-slate-400" data-testid="project-cards-search" />
              {q && <button type="button" onClick={() => setQ("")} className="text-slate-400 hover:text-slate-700" title="Temizle" data-testid="project-cards-search-clear"><X className="w-3 h-3" /></button>}
            </label>
            <button type="button" onClick={onRefresh} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100" title="Proje kartı ve onaylanan hizmet değişiklikleri otomatik yansır (pencere odağında ve 30 sn'de bir). Tıklayınca hemen yenilenir." data-testid="project-cards-refresh">
              <RefreshCw className="w-3 h-3" /> Canlı{updatedAt && <span className="font-mono font-normal" data-testid="project-cards-updated">{updatedAt}</span>}
            </button>
            <Sel icon={CalendarDays} value={String(year || cards.year)} onChange={(v) => onYear(Number(v))} testid="project-cards-year" title="Ciro yılı">
              {cards.years.map((y) => <option key={y} value={y}>{y} cirosu</option>)}
            </Sel>
            <Sel icon={ArrowDownWideNarrow} value={sort} onChange={setSort} testid="project-cards-sort" title="Sıralama">
              {Object.entries(SORTS).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
            </Sel>
          </div>
        </div>
        {active.length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-6 text-center" data-testid="project-cards-empty">{q.trim() ? `"${q.trim()}" ile eşleşen aktif proje yok.` : "Aktif proje yok."}</div>
        ) : <Grid list={active} />}
      </div>
      {finished.length > 0 && (
        <div data-testid="project-cards-finished">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-slate-500" />
            <h2 className="text-base md:text-lg font-semibold text-slate-700">Biten Projeler</h2>
            <span className="text-xs text-slate-500">· {cards.year} yılında tamamlanan {cards.finished.length} proje</span>
          </div>
          <Grid list={finished} finished />
        </div>
      )}
    </div>
  );
}
